import { cajaBancosAPI, ventasAPI } from "@/lib/api";
import { emptyOnForbidden } from "@/lib/httpErrors";
import {
  MODULE_CAJA,
  MODULE_PRESUPUESTOS,
  sessionAccessFingerprint,
  sessionCanAccessModule,
  sessionCanFetchFacturas,
  type SessionModuleAccess,
} from "@/lib/modulePermissions";

export interface CoreDataState {
  presupuestos: any[];
  facturas: any[];
  movimientos: any[];
  cierres: any[];
  loaded: boolean;
  loading: boolean;
  lastLoadedAt: number | null;
}

const state: CoreDataState = {
  presupuestos: [],
  facturas: [],
  movimientos: [],
  cierres: [],
  loaded: false,
  loading: false,
  lastLoadedAt: null,
};

const listeners = new Set<() => void>();
let inFlight: Promise<void> | null = null;
let lastAccess: SessionModuleAccess | null = null;
let lastAccessKey = "";

const emit = () => {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error("appDataStore listener error:", error);
    }
  });
};

const setState = (partial: Partial<CoreDataState>) => {
  Object.assign(state, partial);
  emit();
};

function applyAccess(access?: SessionModuleAccess | null) {
  if (access === undefined) return lastAccess;
  const key = sessionAccessFingerprint(access);
  if (key !== lastAccessKey) {
    lastAccessKey = key;
    state.lastLoadedAt = null;
  }
  lastAccess = access;
  return lastAccess;
}

export const appDataStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  getSnapshot(): CoreDataState {
    return { ...state };
  },

  async ensureLoaded(ttlMs = 30000, access?: SessionModuleAccess | null): Promise<void> {
    const resolved = applyAccess(access);
    const now = Date.now();
    if (state.loaded && state.lastLoadedAt && now - state.lastLoadedAt < ttlMs) {
      return;
    }

    if (inFlight) {
      return inFlight;
    }

    setState({ loading: true });
    inFlight = (async () => {
      try {
        const canPresupuestos = sessionCanAccessModule(resolved, MODULE_PRESUPUESTOS);
        const canFacturas = sessionCanFetchFacturas(resolved);
        const canCaja = sessionCanAccessModule(resolved, MODULE_CAJA);

        const [presupuestosData, facturasData, movsData, cierresData] = await Promise.all([
          canPresupuestos
            ? ventasAPI.listPresupuestos().catch(emptyOnForbidden([]))
            : Promise.resolve([]),
          canFacturas
            ? ventasAPI.listFacturas().catch(emptyOnForbidden([]))
            : Promise.resolve([]),
          canCaja
            ? cajaBancosAPI.listMovimientos().catch(() => [])
            : Promise.resolve([]),
          canCaja
            ? cajaBancosAPI.listCierres().catch(() => [])
            : Promise.resolve([]),
        ]);

        setState({
          presupuestos: presupuestosData,
          facturas: facturasData,
          movimientos: movsData,
          cierres: cierresData,
          loaded: true,
          loading: false,
          lastLoadedAt: Date.now(),
        });
      } catch (error) {
        setState({ loading: false });
        throw error;
      } finally {
        inFlight = null;
      }
    })();

    return inFlight;
  },

  async refreshAll(): Promise<void> {
    state.lastLoadedAt = null;
    return this.ensureLoaded(0, lastAccess);
  },

  async refreshFacturas(): Promise<any[]> {
    if (!sessionCanFetchFacturas(lastAccess)) {
      setState({ facturas: [] });
      return [];
    }
    const facturas = await ventasAPI.listFacturas().catch(emptyOnForbidden([] as any[]));
    setState({ facturas });
    return facturas;
  },

  setFacturas(facturas: any[]) {
    setState({ facturas });
  },

  setPresupuestos(presupuestos: any[]) {
    setState({ presupuestos });
  },

  setMovimientos(movimientos: any[]) {
    setState({ movimientos });
  },

  setCierres(cierres: any[]) {
    setState({ cierres });
  },
};
